import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AddRounded,
  ArrowForwardRounded,
  AutoAwesomeRounded,
  CheckCircleRounded,
  ExpandMoreRounded,
  RestaurantRounded,
  SearchRounded,
  SortRounded,
  StarRounded,
  TimerOutlined,
  TuneRounded,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import RecipeFilterSidebar from '../Recetas/RecipeFilterSidebar';
import ExperimentalPopupProvider from '../ExperimentalPopups/ExperimentalPopupProvider';
import './RecipesPageV2.css';

const initialFilters = {
  ingredients: [],
  momentoDelDia: '',
  restrictions: [],
  difficulty: '',
  cookingType: '',
  recipeCategory: '',
  dishType: '',
  maxTime: 120,
  onlyReviewed: false,
  highProtein: false,
  lowFat: false,
  sortBy: 'recent',
};

const dishTypes = ['Desayuno', 'Snack', 'Entrante', 'Principal', 'Postre', 'Guarnición', 'Bebidas'];
const number = (input) => Number(input) || 0;

const getCreationTime = (recipe) => {
  if (recipe.createdAt) {
    const timestamp = new Date(recipe.createdAt).getTime();
    if (!Number.isNaN(timestamp)) return timestamp;
  }
  const objectId = recipe._id || recipe.id;
  return typeof objectId === 'string' && /^[a-f\d]{24}$/i.test(objectId) ? parseInt(objectId.substring(0, 8), 16) * 1000 : 0;
};

const normalize = (text) => (text || '').toString().normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

const PREVIEW_RECIPES = [
  { _id: 'preview-r1', name: 'Bowl mediterráneo de pollo', mainImage: 'https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=900&q=82', difficulty: 'Baja', prepTime: { minutes: 25 }, momentoDelDia: ['Comida'], tipoPlato: ['Principal'], revisado: true, rating: 4.8, userUsageCount: 6, totalMacronutrients: { kilocalories: 610, protein: 44, carbs: 68, fat: 19 }, createdAt: '2026-07-10' },
  { _id: 'preview-r2', name: 'Salmón al horno con verduras', mainImage: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&w=900&q=82', difficulty: 'Baja', prepTime: { minutes: 35 }, momentoDelDia: ['Cena'], tipoPlato: ['Principal'], revisado: true, rating: 4.7, userUsageCount: 4, totalMacronutrients: { kilocalories: 540, protein: 39, carbs: 35, fat: 25 }, createdAt: '2026-07-08' },
  { _id: 'preview-r3', name: 'Porridge de avena, plátano y canela', mainImage: 'https://images.unsplash.com/photo-1517673400267-0251440c45dc?auto=format&fit=crop&w=900&q=82', difficulty: 'Baja', prepTime: { minutes: 12 }, momentoDelDia: ['Desayuno'], tipoPlato: ['Desayuno'], revisado: true, rating: 4.6, userUsageCount: 9, totalMacronutrients: { kilocalories: 390, protein: 17, carbs: 62, fat: 9 }, createdAt: '2026-07-06' },
  { _id: 'preview-r4', name: 'Curry suave de garbanzos', mainImage: 'https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=900&q=82', difficulty: 'Media', prepTime: { minutes: 40 }, momentoDelDia: ['Comida', 'Cena'], tipoPlato: ['Principal'], rating: 4.5, userUsageCount: 2, totalMacronutrients: { kilocalories: 480, protein: 22, carbs: 71, fat: 13 }, createdAt: '2026-07-04' },
  { _id: 'preview-r5', name: 'Tostada de aguacate y huevo', mainImage: 'https://images.unsplash.com/photo-1525351484163-7529414344d8?auto=format&fit=crop&w=900&q=82', difficulty: 'Baja', prepTime: { minutes: 10 }, momentoDelDia: ['Desayuno'], tipoPlato: ['Desayuno'], revisado: true, rating: 4.4, userUsageCount: 11, totalMacronutrients: { kilocalories: 420, protein: 21, carbs: 39, fat: 22 }, createdAt: '2026-07-02' },
  { _id: 'preview-r6', name: 'Pasta integral con pesto y tomates', mainImage: 'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?auto=format&fit=crop&w=900&q=82', difficulty: 'Baja', prepTime: { minutes: 20 }, momentoDelDia: ['Comida'], tipoPlato: ['Principal'], rating: 4.3, userUsageCount: 3, totalMacronutrients: { kilocalories: 570, protein: 24, carbs: 83, fat: 18 }, createdAt: '2026-06-29' },
  { _id: 'preview-r7', name: 'Ensalada crujiente de quinoa', mainImage: 'https://images.unsplash.com/photo-1505253716362-afaea1d3d1af?auto=format&fit=crop&w=900&q=82', difficulty: 'Baja', prepTime: { minutes: 18 }, momentoDelDia: ['Comida', 'Cena'], tipoPlato: ['Entrante'], revisado: true, rating: 4.6, userUsageCount: 5, totalMacronutrients: { kilocalories: 360, protein: 16, carbs: 49, fat: 12 }, createdAt: '2026-06-26' },
  { _id: 'preview-r8', name: 'Yogur con frutos rojos y nueces', mainImage: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?auto=format&fit=crop&w=900&q=82', difficulty: 'Baja', prepTime: { minutes: 5 }, momentoDelDia: ['Snack', 'Desayuno'], tipoPlato: ['Postre'], rating: 4.2, userUsageCount: 8, totalMacronutrients: { kilocalories: 240, protein: 15, carbs: 24, fat: 11 }, createdAt: '2026-06-22' },
];

const PREVIEW_OPTIONS = {
  ingredients: [],
  restrictions: [{ _id: 'vegetarian', name: 'Vegetariana' }, { _id: 'vegan', name: 'Vegana' }],
  types: [{ _id: 'mediterranean', name: 'Mediterránea' }, { _id: 'asian', name: 'Asiática' }],
  categories: [],
  dishTypes,
};

const RecipeCardV2 = ({ recipe }) => {
  const macros = recipe.totalMacronutrients || {};
  const category = Array.isArray(recipe.categoriaRecetas) ? recipe.categoriaRecetas[0] : recipe.categoriaRecetas;
  const categoryLabel = typeof category === 'string' ? category : category?.nombre;
  const moment = Array.isArray(recipe.momentoDelDia) ? recipe.momentoDelDia[0] : recipe.momentoDelDia;
  const time = number(recipe.prepTime?.hours) * 60 + number(recipe.prepTime?.minutes);
  const difficulty = recipe.difficulty === 'Baja' ? 'Fácil' : recipe.difficulty === 'Alta' ? 'Difícil' : recipe.difficulty || 'Media';

  return (
    <Link className="recipes2-card" to={`/recetas/${recipe._id}`}>
      <div className="recipes2-card-image">
        {recipe.mainImage ? <img src={recipe.mainImage} alt="" /> : <span><RestaurantRounded /></span>}
        <div className="recipes2-card-badges">
          {(categoryLabel || moment) && <span>{categoryLabel || moment}</span>}
          {recipe.revisado && <i title="Receta revisada"><CheckCircleRounded /></i>}
        </div>
        <div className="recipes2-card-image-meta">
          {recipe.userUsageCount > 0 && <span>{recipe.userUsageCount} veces</span>}
          {number(recipe.rating) > 0 && <span><StarRounded /> {number(recipe.rating).toFixed(1)}</span>}
        </div>
      </div>
      <div className="recipes2-card-content">
        <h3>{recipe.name}</h3>
        <div className="recipes2-card-facts">
          <span><TimerOutlined /> {time ? `${time} min` : 'Sin tiempo'}</span>
          <i />
          <span>{difficulty}</span>
        </div>
        <div className="recipes2-card-nutrition">
          <div><span>Calorías</span><strong>{Math.round(number(macros.kilocalories))}<small> kcal</small></strong></div>
          <div className="is-protein"><span>Proteína</span><strong>{Math.round(number(macros.protein))}g</strong></div>
          <div className="is-carbs"><span>Carbos</span><strong>{Math.round(number(macros.carbs))}g</strong></div>
          <div className="is-fat"><span>Grasas</span><strong>{Math.round(number(macros.fat))}g</strong></div>
        </div>
        <span className="recipes2-card-open">Ver receta <ArrowForwardRounded /></span>
      </div>
    </Link>
  );
};

const RecipeGridV2 = ({ recipes, loading }) => {
  if (loading) return <div className="recipes2-grid">{Array.from({ length: 8 }).map((_, index) => <div className="recipes2-skeleton" key={index}><i /><span /><span /></div>)}</div>;
  if (!recipes.length) return <div className="recipes2-empty"><span><SearchRounded /></span><h2>No encontramos ninguna receta</h2><p>Prueba con otro término o elimina alguno de los filtros activos.</p></div>;
  return <div className="recipes2-grid">{recipes.map((recipe) => <RecipeCardV2 recipe={recipe} key={recipe._id} />)}</div>;
};

const RecipesPageV2 = ({ preview = false }) => {
  const { authToken } = useAuth();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState(preview ? PREVIEW_RECIPES : []);
  const [options, setOptions] = useState(preview ? PREVIEW_OPTIONS : { ingredients: [], restrictions: [], types: [], categories: [], dishTypes });
  const [userStats, setUserStats] = useState({});
  const [loading, setLoading] = useState(!preview);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(initialFilters);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [creationDialogOpen, setCreationDialogOpen] = useState(false);
  const [inputMode, setInputMode] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [textInput, setTextInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  useEffect(() => {
    if (preview) return;
    const load = async () => {
      try {
        const [recipesRes, ingredientsRes, restrictionsRes, typesRes, categoriesRes] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/recipes`),
          fetch(`${import.meta.env.VITE_API_URL}/ingredients`),
          fetch(`${import.meta.env.VITE_API_URL}/restricciones`),
          fetch(`${import.meta.env.VITE_API_URL}/tipoDeCocina`),
          fetch(`${import.meta.env.VITE_API_URL}/categoriaRecetas`),
        ]);
        const [recipeData, ingredientData, restrictionData, typeData, categoryData] = await Promise.all([
          recipesRes.json(), ingredientsRes.json(), restrictionsRes.json(), typesRes.json(), categoriesRes.json(),
        ]);
        setRecipes(recipeData);
        setOptions({
          ingredients: ingredientData.map((ingredient) => ({ label: ingredient.name, value: ingredient._id })),
          restrictions: restrictionData,
          types: typeData,
          categories: categoryData,
          dishTypes,
        });
      } catch (error) {
        console.error('Error loading recipes:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [preview]);

  useEffect(() => {
    if (preview || !authToken) return;
    fetch(`${import.meta.env.VITE_API_URL}/recipes/user-stats`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((response) => response.json()).then(setUserStats).catch((error) => console.error('Error loading recipe stats:', error));
  }, [authToken, preview]);

  const ingredientLabels = useMemo(() => options.ingredients.reduce((labels, ingredient) => ({ ...labels, [ingredient.value]: ingredient.label }), {}), [options.ingredients]);
  const restrictionIds = useMemo(() => options.restrictions.reduce((ids, restriction) => {
    const name = normalize(restriction.name);
    if (name.includes('vegetarian')) ids.vegetarian = restriction._id;
    if (name.includes('vegan')) ids.vegan = restriction._id;
    return ids;
  }, {}), [options.restrictions]);

  const filteredRecipes = useMemo(() => recipes.filter((recipe) => {
    if (searchTerm) {
      const ingredientIds = [
        ...(Array.isArray(recipe.ingredients) ? recipe.ingredients.map((item) => item.ingredientId) : []),
        ...(Array.isArray(recipe.components) ? recipe.components.filter((component) => component.refModel === 'Ingredient').map((component) => component.refId) : []),
      ].map((id) => id?.toString?.() || id);
      const ingredientText = ingredientIds.map((id) => ingredientLabels[id]).filter(Boolean).join(' ');
      if (!normalize(recipe.name).includes(normalize(searchTerm)) && !normalize(ingredientText).includes(normalize(searchTerm))) return false;
    }
    if (filters.momentoDelDia) {
      const moments = Array.isArray(recipe.momentoDelDia) ? recipe.momentoDelDia : [recipe.momentoDelDia];
      if (!moments.includes(filters.momentoDelDia)) return false;
    }
    if (filters.difficulty && recipe.difficulty !== filters.difficulty) return false;
    const minutes = number(recipe.prepTime?.hours) * 60 + number(recipe.prepTime?.minutes);
    if (filters.maxTime < 120 && minutes > filters.maxTime) return false;
    if (filters.highProtein) {
      const protein = number(recipe.totalMacronutrients?.protein);
      const calories = number(recipe.totalMacronutrients?.kilocalories);
      if (protein < 30 && (!calories || protein / calories < 0.08)) return false;
    }
    if (filters.lowFat) {
      const fat = number(recipe.totalMacronutrients?.fat);
      const calories = number(recipe.totalMacronutrients?.kilocalories);
      if (!fat && !calories) return false;
      if (fat > 10 && (!calories || (fat * 9) / calories > 0.3)) return false;
    }
    if (filters.restrictions.length && !filters.restrictions.every((id) => recipe.restricciones?.some((restriction) => (typeof restriction === 'object' ? restriction._id : restriction) === id))) return false;
    if (filters.ingredients.length) {
      const used = new Set([
        ...(recipe.ingredients || []).map((item) => item.ingredientId?.toString?.() || item.ingredientId),
        ...(recipe.components || []).filter((component) => component.refModel === 'Ingredient').map((component) => component.refId?.toString?.() || component.refId),
      ]);
      if (!filters.ingredients.every((id) => used.has(id))) return false;
    }
    if (filters.onlyReviewed && !recipe.revisado) return false;
    if (filters.cookingType) {
      const types = Array.isArray(recipe.tipoDeCocina) ? recipe.tipoDeCocina : [recipe.tipoDeCocina];
      if (!types.some((type) => (typeof type === 'object' ? type?._id : type) === filters.cookingType)) return false;
    }
    if (filters.recipeCategory) {
      const categories = Array.isArray(recipe.categoriaRecetas) ? recipe.categoriaRecetas : [recipe.categoriaRecetas];
      if (!categories.some((category) => (typeof category === 'object' ? category?._id : category) === filters.recipeCategory)) return false;
    }
    if (filters.dishType) {
      const types = Array.isArray(recipe.tipoPlato) ? recipe.tipoPlato : [recipe.tipoPlato];
      if (!types.includes(filters.dishType)) return false;
    }
    return true;
  }).map((recipe) => ({ ...recipe, userUsageCount: userStats[recipe._id] || recipe.userUsageCount || 0 })).sort((a, b) => {
    if (filters.sortBy === 'oldest') return getCreationTime(a) - getCreationTime(b);
    if (filters.sortBy === 'timesEaten') return number(b.userUsageCount) - number(a.userUsageCount);
    if (filters.sortBy === 'rating') return number(b.rating) - number(a.rating);
    return getCreationTime(b) - getCreationTime(a);
  }), [filters, ingredientLabels, recipes, searchTerm, userStats]);

  const activeFilterCount = useMemo(() => [filters.ingredients.length, filters.momentoDelDia, filters.restrictions.length, filters.difficulty, filters.cookingType, filters.recipeCategory, filters.dishType, filters.maxTime < 120, filters.onlyReviewed, filters.highProtein, filters.lowFat].filter(Boolean).length, [filters]);
  const reviewedCount = recipes.filter((recipe) => recipe.revisado).length;
  const clearFilters = () => { setFilters(initialFilters); setSearchTerm(''); };
  const toggleRestriction = (id) => id && setFilters((current) => ({ ...current, restrictions: current.restrictions.includes(id) ? current.restrictions.filter((currentId) => currentId !== id) : [...current.restrictions, id] }));
  const toggleMoment = (moment) => setFilters((current) => ({ ...current, momentoDelDia: current.momentoDelDia === moment ? '' : moment }));

  const quickFilters = [
    { key: 'all', label: 'Todas', active: activeFilterCount === 0 && !searchTerm, onClick: clearFilters },
    { key: 'quick', label: 'Menos de 15 min', active: filters.maxTime === 15, onClick: () => setFilters((current) => ({ ...current, maxTime: current.maxTime === 15 ? 120 : 15 })) },
    { key: 'protein', label: 'Alta proteína', active: filters.highProtein, onClick: () => setFilters((current) => ({ ...current, highProtein: !current.highProtein })) },
    { key: 'lowFat', label: 'Bajo en grasas', active: filters.lowFat, onClick: () => setFilters((current) => ({ ...current, lowFat: !current.lowFat })) },
    { key: 'breakfast', label: 'Desayunos', active: filters.momentoDelDia === 'Desayuno', onClick: () => toggleMoment('Desayuno') },
    { key: 'lunch', label: 'Comidas', active: filters.momentoDelDia === 'Comida', onClick: () => toggleMoment('Comida') },
    { key: 'dinner', label: 'Cenas', active: filters.momentoDelDia === 'Cena', onClick: () => toggleMoment('Cena') },
    { key: 'vegetarian', label: 'Vegetarianas', active: filters.restrictions.includes(restrictionIds.vegetarian), disabled: !restrictionIds.vegetarian, onClick: () => toggleRestriction(restrictionIds.vegetarian) },
    { key: 'reviewed', label: 'Revisadas', active: filters.onlyReviewed, onClick: () => setFilters((current) => ({ ...current, onlyReviewed: !current.onlyReviewed })) },
  ];

  const sortLabels = { recent: 'Más recientes', oldest: 'Más antiguas', timesEaten: 'Más cocinadas', rating: 'Mejor valoradas' };

  const submitAIRecipe = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const endpoint = inputMode === 'url' ? 'analyze-recipe-url' : 'analyze-recipe-text';
      const response = await fetch(`${import.meta.env.VITE_API_URL}/${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(inputMode === 'url' ? { url: urlInput } : { text: textInput }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error al analizar la receta');
      }
      const recipeData = await response.json();
      setCreationDialogOpen(false);
      navigate('/recetas/crear', { state: { recipeData } });
    } catch (error) {
      setAiError(error.message);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <ExperimentalPopupProvider>
      <Box className="recipes2-shell">
      <div className="recipes2-page">
        <header className="recipes2-header">
          <div><span className="recipes2-eyebrow">Tu biblioteca</span><h1>¿Qué te apetece cocinar?</h1><p>{loading ? 'Preparando tu recetario…' : `${recipes.length} recetas · ${reviewedCount} revisadas`}</p></div>
          <div className="recipes2-header-actions"><button type="button" onClick={() => { setInputMode(null); setCreationDialogOpen(true); }}><AddRounded /> Crear receta</button></div>
        </header>

        <section className="recipes2-discovery">
          <div className="recipes2-search"><SearchRounded /><input aria-label="Buscar recetas" placeholder="Busca una receta o ingrediente…" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />{searchTerm && <button type="button" onClick={() => setSearchTerm('')}>Borrar</button>}</div>
          <button className="recipes2-filter-button" type="button" aria-label="Abrir filtros" onClick={() => setAdvancedFiltersOpen(true)}><TuneRounded /><span>Filtros</span>{activeFilterCount > 0 && <b>{activeFilterCount}</b>}</button>
          <button className="recipes2-mobile-create" type="button" aria-label="Crear receta" onClick={() => { setInputMode(null); setCreationDialogOpen(true); }}><AddRounded /><span>Crear</span></button>
        </section>

        <div className="recipes2-quick-filters" aria-label="Filtros rápidos">
          {quickFilters.map((filter) => <button type="button" className={filter.active ? 'is-active' : ''} disabled={filter.disabled} onClick={filter.onClick} key={filter.key}>{filter.label}</button>)}
        </div>

        <div className="recipes2-results-heading">
          <div><h2>Explora tus recetas</h2><span>{loading ? 'Cargando…' : `${filteredRecipes.length} ${filteredRecipes.length === 1 ? 'resultado' : 'resultados'}`}</span></div>
          <label><SortRounded /><span>Ordenar</span><select value={filters.sortBy} onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))}>{Object.entries(sortLabels).map(([key, label]) => <option value={key} key={key}>{label}</option>)}</select><ExpandMoreRounded /></label>
        </div>

        <RecipeGridV2 recipes={filteredRecipes} loading={loading} />
      </div>

      <Dialog open={advancedFiltersOpen} onClose={() => setAdvancedFiltersOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '22px', bgcolor: '#f8fafc' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Afinar resultados</DialogTitle>
        <DialogContent><RecipeFilterSidebar filters={filters} setFilters={setFilters} options={options} onClear={clearFilters} /></DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}><Button onClick={clearFilters}>Limpiar todo</Button><Button variant="contained" onClick={() => setAdvancedFiltersOpen(false)} sx={{ background: 'var(--gradient-primary)', borderRadius: 99, px: 3, fontWeight: 800 }}>Ver {filteredRecipes.length} recetas</Button></DialogActions>
      </Dialog>

      <Dialog open={creationDialogOpen} onClose={() => setCreationDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '22px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Añadir una receta</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">Elige cómo quieres incorporarla a tu biblioteca.</Typography>
          {inputMode === null && <Box className="recipes2-create-options">
            <Button onClick={() => navigate('/recetas/crear')}><AddRounded /><span><strong>Manual</strong><small>Completa el formulario</small></span></Button>
            <Button onClick={() => setInputMode('url')}><SearchRounded /><span><strong>Desde una URL</strong><small>La IA importa la receta</small></span></Button>
            <Button onClick={() => setInputMode('text')}><AutoAwesomeRounded /><span><strong>Desde texto</strong><small>Pega cualquier receta</small></span></Button>
          </Box>}
          {inputMode === 'url' && <Box mt={2}><DialogContentText>Introduce la URL y la IA extraerá ingredientes y pasos.</DialogContentText><TextField autoFocus margin="dense" label="URL de la receta" type="url" fullWidth value={urlInput} onChange={(event) => setUrlInput(event.target.value)} /></Box>}
          {inputMode === 'text' && <Box mt={2}><DialogContentText>Pega el texto completo de la receta.</DialogContentText><TextField autoFocus margin="dense" label="Texto de la receta" multiline minRows={7} fullWidth value={textInput} onChange={(event) => setTextInput(event.target.value)} /></Box>}
          {aiLoading && <Box display="flex" justifyContent="center" gap={2} py={2}><CircularProgress size={24} /><Typography>Analizando receta…</Typography></Box>}
          {aiError && <Alert severity="error" sx={{ mt: 2 }}>{aiError}</Alert>}
        </DialogContent>
        <DialogActions><Button onClick={() => { setCreationDialogOpen(false); setInputMode(null); setAiError(null); }}>Cancelar</Button>{inputMode && !aiLoading && <Button variant="contained" onClick={submitAIRecipe} disabled={inputMode === 'url' ? !urlInput : !textInput.trim()}>Generar</Button>}</DialogActions>
        </Dialog>
      </Box>
    </ExperimentalPopupProvider>
  );
};

export default RecipesPageV2;
